import React, { useState, useEffect, useRef, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useTrackEvent from "@/hooks/useTrackEvent";

interface Props {
  value: string;
  onChange: (prompt: string) => void;
  configName?: string;
  onOpenVersionHistory?: () => void;
}

const SECTIONS = [
  {
    key: "identity",
    label: "Identity",
    marker: "[Identity]",
    help: "Who is this bot? What company does it represent?",
    placeholder: "You are a helpful, knowledgeable AI assistant for {Company Name}. You specialize in {products/services}.",
    defaultText: "You are a helpful, knowledgeable AI assistant. You represent this company and help visitors get accurate answers quickly. You are friendly, professional, and always honest about what you know and don't know.",
    generateHint: "Describe your company and the bot's role (e.g. 'We sell arborist supplies and this bot helps customers find gear').",
    icon: "👤",
  },
  {
    key: "scope",
    label: "Scope & Topics",
    marker: "[Scope]",
    help: "What topics can the bot discuss? What should it avoid?",
    placeholder: "You may only provide information related to:\n- {Product category 1}\n- {Product category 2}\n- General questions about {your industry}",
    defaultText: "Answer questions about this company's products, services, policies, and support topics. If a question is outside your knowledge, say so honestly and offer to connect the visitor with a human agent.",
    generateHint: "What topics should the bot cover or avoid? Mention any restricted topics.",
    icon: "📋",
  },
  {
    key: "rules",
    label: "Rules & Boundaries",
    marker: "[Rules]",
    help: "Specific instructions, restrictions, or behaviors the bot must follow.",
    placeholder: "You must not:\n- Make up information\n- Discuss competitors\n- Share pricing without confirmation\n\nAlways:\n- Ask for the order number before looking up orders\n- Offer to transfer to a human if you can't help",
    defaultText: "Never make up information you don't have. Never discuss competitors or make comparisons. Always offer to escalate to a human agent when you can't fully resolve the visitor's issue.",
    generateHint: "List any hard rules: what the bot must never do, what it must always do, and any compliance requirements.",
    icon: "🛡️",
  },
  {
    key: "style",
    label: "Response Style",
    marker: "[Style]",
    help: "How should the bot communicate? Length, format, follow-ups.",
    placeholder: "Provide concise but informative responses. Anticipate common follow-up questions. Offer links only when they add value.",
    defaultText: "Keep responses concise — 2 to 4 sentences unless more detail is clearly needed. Use plain language. Ask one clarifying question at a time. End responses with a helpful follow-up question when appropriate.",
    generateHint: "Describe the tone: formal vs casual, short vs detailed, emoji usage, any specific phrasing to use or avoid.",
    icon: "✍️",
  },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

interface SectionUiState {
  generating: boolean;
  input: string;
  showGenerate: boolean;
  previousText?: string;
}

function parseSections(prompt: string): Record<SectionKey, string> {
  const result: Record<string, string> = { identity: "", scope: "", rules: "", style: "" };
  if (!prompt?.trim()) return result;

  const markerPattern = /\[(Identity|Scope|Rules|Style)\]\n?/gi;
  const markers: { key: string; index: number }[] = [];
  let match;
  while ((match = markerPattern.exec(prompt)) !== null) {
    markers.push({ key: match[1].toLowerCase(), index: match.index });
  }

  if (markers.length === 0) {
    result.identity = prompt.trim();
    return result;
  }

  for (let i = 0; i < markers.length; i++) {
    const start = markers[i].index + markers[i].key.length + 3;
    const end = i + 1 < markers.length ? markers[i + 1].index : prompt.length;
    result[markers[i].key] = prompt.slice(start, end).trim();
  }

  const beforeFirst = prompt.slice(0, markers[0].index).trim();
  if (beforeFirst) {
    result.identity = beforeFirst + (result.identity ? "\n\n" + result.identity : "");
  }

  return result;
}

function assembleSections(sections: Record<SectionKey, string>): string {
  return SECTIONS
    .filter((s) => sections[s.key]?.trim())
    .map((s) => `${s.marker}\n${sections[s.key].trim()}`)
    .join("\n\n");
}

function autoResize(el: HTMLTextAreaElement | null, minHeight = 80) {
  if (!el) return;
  el.style.height = "0px";
  el.style.height = Math.max(minHeight, el.scrollHeight) + "px";
}

export default function PromptEditor({ value, onChange, configName, onOpenVersionHistory }: Props) {
  const [sections, setSections] = useState<Record<SectionKey, string>>(parseSections(value));
  const [generating, setGenerating] = useState(false);
  const [generateInput, setGenerateInput] = useState("");
  const [showGenerate, setShowGenerate] = useState(false);
  const [sectionUi, setSectionUi] = useState<Record<string, SectionUiState>>({});
  const api = useApi();
  const { track } = useTrackEvent();
  const sectionRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  useEffect(() => {
    setSections(parseSections(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    Object.values(sectionRefs.current).forEach((el) => autoResize(el, 80));
  }, [sections]);

  const setSectionUiField = useCallback(
    (key: string, patch: Partial<SectionUiState>) =>
      setSectionUi((prev) => {
        const existing: SectionUiState = prev[key] ?? { generating: false, input: "", showGenerate: false };
        return { ...prev, [key]: { ...existing, ...patch } };
      }),
    []
  );

  const handleSectionChange = useCallback(
    (key: SectionKey, text: string) => {
      const updated = { ...sections, [key]: text };
      setSections(updated);
      onChange(assembleSections(updated));
    },
    [sections, onChange]
  );

  async function handleGenerate() {
    if (!generateInput.trim()) return;
    setGenerating(true);
    try {
      const response = await api.admin.post("Copilot/generate-prompt", {
        description: generateInput.trim(),
        configName: configName ?? "",
      });
      if (response.ok) {
        const result = await response.json();
        if (result.prompt) {
          const parsed = parseSections(result.prompt);
          setSections(parsed);
          onChange(result.prompt);
          track("feature_click", "AIBot/PromptSaved");
          setShowGenerate(false);
          setGenerateInput("");
        }
      }
    } catch (err) {
      console.error("Prompt generation failed:", err);
    } finally {
      setGenerating(false);
    }
  }

  async function handleSectionGenerate(sectionKey: SectionKey) {
    const ui = sectionUi[sectionKey];
    const input = ui?.input?.trim();
    if (!input) return;
    const previousText = sections[sectionKey];
    setSectionUiField(sectionKey, { generating: true, previousText });
    const section = SECTIONS.find((s) => s.key === sectionKey)!;
    try {
      const response = await api.admin.post("Copilot/generate-prompt-section", {
        section: sectionKey,
        description: input,
        configName: configName ?? "",
        existingPrompt: value,
      });
      if (response.ok) {
        const result = await response.json();
        if (result.text) {
          handleSectionChange(sectionKey, result.text);
          setSectionUiField(sectionKey, { generating: false, input: "", showGenerate: false, previousText });
          track("feature_click", `AIBot/SectionGenerated/${sectionKey}`);
          return;
        }
      }
    } catch {
      // fall through to default below
    }
    handleSectionChange(sectionKey, section.defaultText);
    setSectionUiField(sectionKey, { generating: false, showGenerate: false, previousText });
  }

  function handleSectionUndo(sectionKey: SectionKey) {
    const ui = sectionUi[sectionKey];
    if (ui?.previousText !== undefined) {
      handleSectionChange(sectionKey, ui.previousText);
      setSectionUiField(sectionKey, { previousText: undefined });
    }
  }

  return (
    <div className="py-2">
      {/* Header */}
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="font-semibold text-sm text-slate-800">Bot Instructions</div>
          <div className="text-xs text-slate-400 mt-0.5">
            These four sections combine into one prompt the bot follows in every conversation.
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4 pt-0.5">
          {onOpenVersionHistory && (
            <button
              type="button"
              onClick={onOpenVersionHistory}
              className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8Z" />
                <path d="M8 5v3.5l2 1.5" strokeLinecap="round" />
              </svg>
              History
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowGenerate((v) => !v)}
            className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md transition-all ${
              showGenerate
                ? "bg-slate-100 text-slate-600 hover:bg-slate-200"
                : "bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200"
            }`}
          >
            {showGenerate ? (
              <>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M1 1l8 8M9 1L1 9" strokeLinecap="round" />
                </svg>
                Cancel
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5z" />
                </svg>
                Generate All with AI
              </>
            )}
          </button>
        </div>
      </div>

      {/* Generate All Panel */}
      {showGenerate && (
        <div className="mb-4 rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 overflow-hidden">
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base">✨</span>
              <div className="font-semibold text-sm text-violet-900">Generate all sections</div>
            </div>
            <div className="text-xs text-violet-600 mb-3 leading-relaxed">
              Describe your business and what the bot should do — we'll write all four sections for you.
            </div>
            <textarea
              value={generateInput}
              onChange={(e) => setGenerateInput(e.target.value)}
              placeholder="Example: We sell arborist supplies and tree climbing gear. The bot should help customers find products, answer questions about tree care equipment, and offer to connect them with a specialist for custom orders."
              rows={3}
              className="w-full px-3 py-2.5 bg-white border border-violet-200 rounded-lg text-sm resize-none outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 placeholder:text-slate-400 leading-relaxed"
              autoFocus
            />
          </div>
          <div className="px-4 pb-4 flex justify-end">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || !generateInput.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {generating ? (
                <>
                  <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" />
                  </svg>
                  Generating…
                </>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5z" />
                  </svg>
                  Generate All Sections
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Section Blocks */}
      <div className="flex flex-col gap-3">
        {SECTIONS.map((s, idx) => {
          const current = sections[s.key];
          const ui = sectionUi[s.key];
          const isAtDefault = current === s.defaultText;
          const isEmpty = !current.trim();
          const hasUndo = ui?.previousText !== undefined && !ui?.showGenerate;

          return (
            <div
              key={s.key}
              className="rounded-xl border border-slate-200 bg-white overflow-hidden transition-shadow hover:shadow-sm"
            >
              {/* Undo strip — appears at top when AI has just rewritten */}
              {hasUndo && (
                <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b border-amber-200">
                  <div className="flex items-center gap-2 text-xs text-amber-700">
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path d="M2.5 8a5.5 5.5 0 1 1 .9 3M2.5 5v3h3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>Rewritten by AI</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSectionUndo(s.key)}
                    className="text-xs font-semibold text-amber-700 hover:text-amber-900 transition-colors"
                  >
                    Undo →
                  </button>
                </div>
              )}

              {/* Section Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="text-base leading-none select-none">{s.icon}</span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-800 leading-none mb-0.5">{s.label}</div>
                    <div className="text-xs text-slate-400 leading-snug truncate">{s.help}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 ml-3">
                  {isEmpty && (
                    <button
                      type="button"
                      onClick={() => handleSectionChange(s.key, s.defaultText)}
                      className="text-xs text-blue-500 hover:text-blue-700 font-medium transition-colors"
                    >
                      Use default
                    </button>
                  )}
                  {!isEmpty && !isAtDefault && !hasUndo && (
                    <button
                      type="button"
                      onClick={() => handleSectionChange(s.key, s.defaultText)}
                      className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      Reset
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setSectionUiField(s.key, { showGenerate: !ui?.showGenerate })}
                    className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-all ${
                      ui?.showGenerate
                        ? "text-slate-500 hover:text-slate-700"
                        : "text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                    }`}
                    title="Rewrite this section with AI — you can undo afterward"
                  >
                    {ui?.showGenerate ? (
                      "Cancel"
                    ) : (
                      <>
                        <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" className="shrink-0">
                          <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5z" />
                        </svg>
                        Rewrite
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* AI Rewrite Panel */}
              {ui?.showGenerate && (
                <div className="px-4 py-3 bg-violet-50 border-b border-violet-100">
                  <div className="text-xs text-violet-600 mb-2 leading-relaxed">{s.generateHint}</div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={ui.input ?? ""}
                      onChange={(e) => setSectionUiField(s.key, { input: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); handleSectionGenerate(s.key); }
                      }}
                      placeholder="Describe what this section should say…"
                      className="flex-1 px-3 py-1.5 bg-white border border-violet-200 rounded-lg text-xs outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 placeholder:text-slate-400"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => handleSectionGenerate(s.key)}
                      disabled={ui.generating || !ui.input?.trim()}
                      className="px-3 py-1.5 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      title="Replaces current text — you can undo afterward"
                    >
                      {ui.generating ? (
                        <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" />
                        </svg>
                      ) : "Replace"}
                    </button>
                  </div>
                </div>
              )}

              {/* Textarea */}
              <textarea
                ref={(el) => {
                  sectionRefs.current[s.key] = el;
                  autoResize(el, 80);
                }}
                value={current}
                onChange={(e) => {
                  handleSectionChange(s.key, e.target.value);
                  autoResize(e.target, 80);
                }}
                placeholder={s.placeholder}
                className="w-full px-4 py-3 text-sm text-slate-700 border-0 outline-none overflow-hidden resize-none placeholder:text-slate-300 leading-relaxed bg-white"
              />
            </div>
          );
        })}

        <div className="flex items-center gap-1.5 px-1 text-xs text-slate-400">
          <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="8" cy="8" r="6.5" />
            <path d="M8 7v5M8 5.5v.5" strokeLinecap="round" />
          </svg>
          Sections are combined in order. The bot sees all four together as one set of instructions.
        </div>
      </div>
    </div>
  );
}
